/**
 * Composable for calculating holiday discount pricing
 *
 * Features:
 * - Holiday period (Dec 24 - Jan 4, UTC): 15% Pearl, 20% others
 * - February / Chinese New Year (Feb 1 - last day of Feb, UTC): 10% for all; ends March 1
 * - March spring (Mar 5 - Mar 15 inclusive, UTC): per-token percentages
 * - March St. Patrick (Mar 16 - Mar 18 inclusive, UTC): per-token percentages
 * - Easter (Apr 3 - Apr 6 inclusive, UTC): per-token percentages
 * - Returns pricing information for display
 * - Inactive on demo environment
 */

import { useRuntimeConfig } from 'nuxt/app';

interface HolidayDiscountResult {
  isActive: ComputedRef<boolean>;
  originalPrice: ComputedRef<number>;
  discountedPrice: ComputedRef<number>;
  discountPercent: ComputedRef<number>;
}

const PEARL_DISCOUNT = 15;
const OTHER_DISCOUNT = 20;
const FEBRUARY_DISCOUNT = 10;

const MARCH_DISCOUNTS: Record<string, number> = {
  pearl: 15,
  coal: 20,
  goldOre: 10,
  wood: 20,
  ironOre: 10,
  clay: 10,
  sand: 10
};

const ST_PATRICK_DISCOUNTS: Record<string, number> = {
  pearl: 0,
  coal: 10,
  goldOre: 20,
  wood: 10,
  ironOre: 10,
  clay: 10,
  sand: 10
};

const EASTER_DISCOUNTS: Record<string, number> = {
  pearl: 10,
  coal: 10,
  goldOre: 10,
  wood: 15,
  ironOre: 10,
  clay: 10,
  sand: 10
};

const GOLDEN_WEEK_DISCOUNTS: Record<string, number> = {
  pearl: 5,
  coal: 15,
  goldOre: 20,
  wood: 5,
  ironOre: 15,
  clay: 5,
  sand: 5
};

/**
 * Check if current date (UTC+0) is within holiday discount period
 * @param originalPrice - The original price of the pack
 * @param tokenContract - Token contract name (e.g. 'pearl', 'coal', 'goldOre') for per-token March discounts
 * @returns Object with pricing information
 */
export default function useHolidayDiscount(
  originalPrice: Ref<number> | number,
  tokenContract: Ref<string> | string
): HolidayDiscountResult {
  const isActive = ref(false);
  const discountPercent = ref(0);

  // Convert to refs if needed
  const priceRef = typeof originalPrice === 'number' ? ref(originalPrice) : originalPrice;
  const tokenContractRef = typeof tokenContract === 'string' ? ref(tokenContract) : tokenContract;

  const config = useRuntimeConfig();

  const checkDiscountStatus = () => {
    if (import.meta.server) {
      isActive.value = false;
      discountPercent.value = 0;
      return;
    }
    if (config.public.env === 'demo') {
      isActive.value = false;
      discountPercent.value = 0;
      return;
    }

    const now = new Date();
    const currentYear = now.getUTCFullYear();

    // Get current UTC date components
    const currentMonth = now.getUTCMonth(); // 0-11 (0 = January)
    const currentDate = now.getUTCDate(); // 1-31

    const isJanuary = currentMonth === 0; // January
    const yearForDec = isJanuary ? currentYear - 1 : currentYear;
    const yearForJan = isJanuary ? currentYear : currentYear + 1;

    const holidayStart = new Date(Date.UTC(yearForDec, 11, 24, 0, 0, 0, 0));
    const holidayEnd = new Date(Date.UTC(yearForJan, 0, 4, 23, 59, 59, 999));
    const lastDayFeb = new Date(Date.UTC(currentYear, 2, 0)).getUTCDate();
    const februaryStart = new Date(Date.UTC(currentYear, 1, 1, 0, 0, 0, 0));
    const februaryEnd = new Date(Date.UTC(currentYear, 1, lastDayFeb, 23, 59, 59, 999));
    const marchSpringStart = new Date(Date.UTC(currentYear, 2, 5, 0, 0, 0, 0));
    const marchSpringEnd = new Date(Date.UTC(currentYear, 2, 15, 23, 59, 59, 999));
    const marchStPatrickStart = new Date(Date.UTC(currentYear, 2, 16, 0, 0, 0, 0));
    const marchStPatrickEnd = new Date(Date.UTC(currentYear, 2, 18, 23, 59, 59, 999));
    const easterStart = new Date(Date.UTC(currentYear, 3, 3, 0, 0, 0, 0));
    const easterEnd = new Date(Date.UTC(currentYear, 3, 6, 23, 59, 59, 999));
    const goldenWeekStart = new Date(Date.UTC(currentYear, 3, 29, 0, 0, 0, 0));
    const goldenWeekEnd = new Date(Date.UTC(currentYear, 4, 6, 23, 59, 59, 999));

    const nowUTC = new Date(
      Date.UTC(currentYear, currentMonth, currentDate, now.getUTCHours(), now.getUTCMinutes(), now.getUTCSeconds())
    );

    const isInHolidayPeriod = nowUTC >= holidayStart && nowUTC <= holidayEnd;
    const isInFebruaryPeriod = nowUTC >= februaryStart && nowUTC <= februaryEnd;
    const isInMarchSpringPeriod = nowUTC >= marchSpringStart && nowUTC <= marchSpringEnd;
    const isInMarchStPatrickPeriod = nowUTC >= marchStPatrickStart && nowUTC <= marchStPatrickEnd;
    const isInEasterPeriod = nowUTC >= easterStart && nowUTC <= easterEnd;
    const isInGoldenWeekPeriod = nowUTC >= goldenWeekStart && nowUTC <= goldenWeekEnd;
    const contract = tokenContractRef.value;

    if (isInFebruaryPeriod) {
      isActive.value = true;
      discountPercent.value = FEBRUARY_DISCOUNT;
    } else if (isInMarchStPatrickPeriod) {
      const percent = ST_PATRICK_DISCOUNTS[contract] ?? 0;
      isActive.value = percent > 0;
      discountPercent.value = percent;
    } else if (isInMarchSpringPeriod) {
      const marchPercent = MARCH_DISCOUNTS[contract] ?? 0;
      isActive.value = marchPercent > 0;
      discountPercent.value = marchPercent;
    } else if (isInEasterPeriod) {
      const easterPercent = EASTER_DISCOUNTS[contract] ?? 0;
      isActive.value = easterPercent > 0;
      discountPercent.value = easterPercent;
    } else if (isInGoldenWeekPeriod) {
      const goldenWeekPercent = GOLDEN_WEEK_DISCOUNTS[contract] ?? 0;
      isActive.value = goldenWeekPercent > 0;
      discountPercent.value = goldenWeekPercent;
    } else if (isInHolidayPeriod) {
      isActive.value = true;
      discountPercent.value = contract === 'pearl' ? PEARL_DISCOUNT : OTHER_DISCOUNT;
    } else {
      isActive.value = false;
      discountPercent.value = 0;
    }
  };

  // Calculate discounted price (reactive to all dependencies)
  const discountedPrice = computed(() => {
    if (!isActive.value || discountPercent.value === 0) {
      return priceRef.value;
    }
    const discount = (priceRef.value * discountPercent.value) / 100;
    return Math.round((priceRef.value - discount) * 100) / 100; // Round to 2 decimal places
  });

  // Check on mount and set up interval for date transitions
  let interval: ReturnType<typeof setInterval> | null = null;

  onMounted(() => {
    if (import.meta.client) {
      checkDiscountStatus();

      // Re-check every hour to catch date transitions
      interval = setInterval(
        () => {
          checkDiscountStatus();
        },
        60 * 60 * 1000
      ); // 1 hour
    }
  });

  onBeforeUnmount(() => {
    if (interval) {
      clearInterval(interval);
      interval = null;
    }
  });

  // Watch for price or token contract changes
  watch([priceRef, tokenContractRef], () => {
    if (import.meta.client) {
      checkDiscountStatus();
    }
  });

  return {
    isActive: computed(() => isActive.value),
    originalPrice: computed(() => priceRef.value),
    discountedPrice,
    discountPercent: computed(() => discountPercent.value)
  };
}
